import React, { useEffect, useState, useMemo } from 'react';
import {
  ColumnDef,
  SortingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  useReactTable,
  ColumnFiltersState,
} from '@tanstack/react-table';
import { ArrowUpDown, ChevronDown } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { getAllFeedback, FeedbackWithUser } from '../../services/Feedback';

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const formatKey = (key: string) =>
  key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const FeedbackListPage: React.FC = () => {
  const [feedbackList, setFeedbackList] = useState<FeedbackWithUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [selectedVersion, setSelectedVersion] = useState<string>('all');

  useEffect(() => {
    const fetchFeedback = async () => {
      const result = await getAllFeedback();
      if (result.success && result.data) {
        setFeedbackList(result.data);
      }
      setLoading(false);
    };
    fetchFeedback();
  }, []);

  // Derive unique form versions from data
  const formVersions = useMemo(() => {
    const versions = new Set(feedbackList.map((f) => f.formVersion));
    return Array.from(versions).sort();
  }, [feedbackList]);

  // Filter by selected version
  const filteredByVersion = useMemo(() => {
    if (selectedVersion === 'all') return feedbackList;
    return feedbackList.filter((f) => f.formVersion === selectedVersion);
  }, [feedbackList, selectedVersion]);

  // Dynamically discover all response keys from the actual data
  const responseKeys = useMemo(() => {
    const keys = new Set<string>();
    filteredByVersion.forEach((f) => {
      Object.keys(f.responses || {}).forEach((k) => keys.add(k));
    });
    return Array.from(keys);
  }, [filteredByVersion]);

  // Build columns dynamically
  const columns = useMemo<ColumnDef<FeedbackWithUser>[]>(() => {
    const fixed: ColumnDef<FeedbackWithUser>[] = [
      {
        accessorKey: 'userEmail',
        header: ({ column }) => (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            User
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => (
          <div className="font-medium">{row.getValue('userEmail')}</div>
        ),
      },
      {
        accessorKey: 'formVersion',
        header: 'Version',
        cell: ({ row }) => (
          <Badge variant="secondary">{row.getValue('formVersion')}</Badge>
        ),
      },
      {
        accessorKey: 'createdAt',
        header: ({ column }) => (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Date
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => (
          <div className="whitespace-nowrap">
            {formatDate(row.getValue('createdAt'))}
          </div>
        ),
      },
    ];

    const dynamic: ColumnDef<FeedbackWithUser>[] = responseKeys.map((key) => ({
      id: `response_${key}`,
      header: formatKey(key),
      accessorFn: (row: FeedbackWithUser) => {
        const val = row.responses?.[key];
        if (val === undefined || val === null || val === '') return null;
        return val;
      },
      cell: ({ getValue }: { getValue: () => any }) => {
        const val = getValue();
        if (val === null) return <span className="text-muted-foreground">-</span>;
        return <div className="max-w-[250px] truncate">{String(val)}</div>;
      },
    }));

    return [...fixed, ...dynamic];
  }, [responseKeys]);

  const table = useReactTable({
    data: filteredByVersion,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting,
      columnFilters,
    },
  });

  if (loading) {
    return (
      <div className="p-8">
        <h1 className="text-3xl font-bold mb-6">Feedback Responses</h1>
        <div className="space-y-4">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <h1 className="text-3xl font-bold mb-6">Feedback Responses</h1>

      <div className="w-full">
        <div className="flex items-center py-4 gap-2">
          <Input
            placeholder="Filter by user email..."
            value={(table.getColumn('userEmail')?.getFilterValue() as string) ?? ''}
            onChange={(e) =>
              table.getColumn('userEmail')?.setFilterValue(e.target.value)
            }
            className="max-w-sm"
          />
          {formVersions.length > 1 && (
            <Select value={selectedVersion} onValueChange={setSelectedVersion}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Version" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All versions</SelectItem>
                {formVersions.map((v) => (
                  <SelectItem key={v} value={v}>
                    v{v}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="ml-auto">
                Columns <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {table
                .getAllColumns()
                .filter((column) => column.getCanHide())
                .map((column) => (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    className="capitalize"
                    checked={column.getIsVisible()}
                    onCheckedChange={(value) =>
                      column.toggleVisibility(!!value)
                    }
                  >
                    {column.id.startsWith('response_')
                      ? formatKey(column.id.replace('response_', ''))
                      : column.id}
                  </DropdownMenuCheckboxItem>
                ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <Card>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-24 text-center"
                    >
                      <p className="text-muted-foreground">
                        No feedback submissions yet.
                      </p>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </Card>

        <div className="flex items-center justify-end py-4">
          <p className="text-sm text-muted-foreground">
            {filteredByVersion.length} submission{filteredByVersion.length !== 1 ? 's' : ''}
            {selectedVersion !== 'all' && ` (v${selectedVersion})`}
          </p>
        </div>
      </div>
    </div>
  );
};

export default FeedbackListPage;
